import fr from "./fr";
import en from "./en";
import tn from "./tn";

// Supported languages, in the order of the switch. `tn` = Tunisian Arabic (Derja), written in
// Arabic script.
export const LANGS = {
  fr: { code: "fr", htmlLang: "fr",    dir: "ltr", name: "Français", short: "FR" },
  en: { code: "en", htmlLang: "en",    dir: "ltr", name: "English",  short: "EN" },
  tn: { code: "tn", htmlLang: "ar-TN", dir: "rtl", name: "تونسي",    short: "TN" },
};
export const DEFAULT_LANG = "fr";

// Language picker on or off. The owner hid it for a while and turned it back
// on. Set it to false to hide it again: the picker then renders nothing and
// everybody gets French, whatever they chose before and whatever their phone
// speaks (`?lang=tn` in the address still shows Tunisian, for that visit
// only). Nothing else to change either way.
export const LANG_SWITCH = true;

const DICTS = { fr, en, tn };
const STORAGE_KEY = "lang";

// Unicode bidi controls. Translated strings are wrapped in an RTL isolate so
// they read correctly even inside LTR containers (the game board never mirrors).
const LRM = "‎";
const LRI = "⁦";
const RLI = "⁧";
const FSI = "⁨";
const PDI = "⁩";

// The time zones of Tunisia and of France (mainland, Corsica and overseas).
const TN_ZONES = new Set(["Africa/Tunis"]);
const FR_ZONES = new Set([
  "Europe/Paris", "Indian/Reunion", "Indian/Mayotte", "America/Guadeloupe", "America/Martinique",
  "America/Cayenne", "America/Miquelon", "America/St_Barthelemy", "America/Marigot",
  "Pacific/Tahiti", "Pacific/Marquesas", "Pacific/Gambier", "Pacific/Noumea", "Pacific/Wallis",
]);

function deviceTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; }
}

function deviceLanguages() {
  if (typeof navigator === "undefined") return [];
  return navigator.languages?.length ? [...navigator.languages] : [navigator.language || ""];
}

// The country the phone is in, "TN", "FR" or null (anywhere else): from its time
// zone, which the mobile network sets, with nothing sent anywhere. Only when the
// zone says nothing (none, or UTC as on some computers) does the region of its
// languages count ("ar-TN" → TN, "fr-FR" → FR).
export function deviceCountry(timeZone = deviceTimeZone(), languages = deviceLanguages()) {
  if (timeZone && !/^(UTC|GMT|Etc\/)/i.test(timeZone)) {
    if (TN_ZONES.has(timeZone)) return "TN";
    if (FR_ZONES.has(timeZone)) return "FR";
    return null;
  }
  for (const tag of languages) {
    const region = (tag || "").split(/[-_]/)[1]?.toUpperCase();
    if (region === "TN" || region === "FR") return region;
  }
  return null;
}

// Before the player picks a language: Tunisian in Tunisia, French in France,
// English anywhere else (owner's rule, October 2026; it was the phone's
// language before).
export function deviceLang(country = deviceCountry()) {
  if (country === "TN") return "tn";
  if (country === "FR") return "fr";
  return "en";
}

function detectLang() {
  try {
    // ?lang=tn lets a shared link open directly in a given language.
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    if (fromUrl && LANGS[fromUrl]) {
      if (LANG_SWITCH) localStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    if (!LANG_SWITCH) return DEFAULT_LANG;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGS[saved]) return saved;
  } catch {
    if (!LANG_SWITCH) return DEFAULT_LANG;
  }
  return deviceLang();
}

let current = DEFAULT_LANG;
const listeners = new Set();

const lookup = (dict, key) =>
  key.split(".").reduce((node, part) => (node == null ? undefined : node[part]), dict);

function applyToDocument() {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.lang = LANGS[current].htmlLang;
  el.dir = LANGS[current].dir;
  document.title = lookup(DICTS[current], "meta.title") || document.title;
}

if (typeof window !== "undefined") {
  current = detectLang();
  applyToDocument();
}

export const getLang = () => current;
export const isRtl = () => LANGS[current].dir === "rtl";

export function setLang(code) {
  if (!LANGS[code] || code === current) return;
  current = code;
  try { localStorage.setItem(STORAGE_KEY, code); } catch {}
  applyToDocument();
  listeners.forEach((fn) => fn());
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// True for a string that already went through t() in an RTL language.
export const isWrapped = (s) => typeof s === "string" && s.startsWith(RLI);

// Direction to give a text bubble. Decided by the language the sentence was
// rendered in, not by its letters: a French line may carry an Arabic name.
export const textDir = (s) => (isWrapped(s) ? "rtl" : "ltr");

// Keep an LTR chunk (card list, untranslated French) intact inside RTL text.
export const ltr = (s) => LRI + s + PDI;

// Names, card lists… are isolated so their punctuation can't jump sides.
const isolate = (v) => (typeof v === "number" || v === "" ? String(v) : FSI + v + PDI);

// A leading "+" (or "−") would otherwise flip to the right of its number in RTL ("50+").
const wrapRtl = (s) => RLI + s.replace(/[+−](?=\d)/g, (sign) => LRM + sign) + PDI;

// Entry for `key` in the current language, falling back to French.
function resolve(key) {
  const own = lookup(DICTS[current], key);
  if (own != null) return { entry: own, rtl: isRtl() };
  return { entry: lookup(DICTS[DEFAULT_LANG], key), rtl: false };
}

// An entry can be a string, a function of `vars` (plurals), or an array of
// variants. Variants are picked at random unless `seed` is given, so only use
// them for event-time strings (feed, toasts) — never directly in render.
function pickEntry(entry, vars, seed) {
  let e = entry;
  if (Array.isArray(e)) {
    if (e.length === 0) return "";
    const i = seed == null ? Math.floor(Math.random() * e.length) : Math.abs(seed) % e.length;
    e = e[i];
  }
  if (typeof e === "function") e = e(vars || {});
  return String(e);
}

const fill = (tpl, vars, rtl) =>
  tpl.replace(/\{(\w+)\}/g, (m, k) =>
    vars && vars[k] != null ? (rtl ? isolate(vars[k]) : String(vars[k])) : m
  );

export function t(key, vars, seed) {
  const { entry, rtl } = resolve(key);
  if (entry == null) return key;
  const out = fill(pickEntry(entry, vars, seed), vars, rtl);
  // An empty string stays empty, so `t(key) && …` can skip it.
  return rtl && out ? wrapRtl(out) : out;
}

// All variants of an array entry (e.g. the winner punchlines).
export function tList(key) {
  const { entry, rtl } = resolve(key);
  if (!Array.isArray(entry)) return entry == null ? [] : [t(key)];
  return entry.map((e) => (rtl ? wrapRtl(String(e)) : String(e)));
}

// Unwrapped template, for tRich() which interleaves React nodes.
export function rawTemplate(key, vars) {
  const { entry } = resolve(key);
  return entry == null ? key : pickEntry(entry, vars, 0);
}

// Gettext-style lookup of a fixed backend sentence (keyed by its French text).
export function tExact(text) {
  const hit = DICTS[current].srvExact?.[text];
  if (hit == null) return null;
  return isRtl() ? wrapRtl(hit) : hit;
}
